import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import { CompleteBasketItem } from '../../../providers/BasketProvider';

interface Props {
  item: CompleteBasketItem;
  className?: string;
}

/**
 * Per-line breakdown for a free-unit offer (BOGO / free item):
 * "1 × €4.00 paid · 1 × free". Renders nothing unless the item carries an
 * `offer_breakdown` with at least one free unit (set by the offer handlers).
 */
const OfferLineBreakdown: FC<Props> = ({ item, className }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:Offers:OfferLineBreakdown' });
  const { formatCurrency } = useAppMethods();

  const breakdown = item.offer_breakdown;

  if (!breakdown || breakdown.freeQuantity <= 0) {
    return null;
  }

  const { paidQuantity, freeQuantity, unitPrice } = breakdown;

  const label =
    paidQuantity > 0
      ? t('paid_and_free', { paidQuantity, price: formatCurrency(unitPrice), freeQuantity })
      : t('all_free', { freeQuantity });

  return (
    <p data-test="offer-line-breakdown" className={classnames('text-xs text-muted-foreground', className)}>
      {label}
    </p>
  );
};

export default OfferLineBreakdown;
